import { FC, useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner-native";

import { DuplicateHouseholdMemberDialog } from "@/blocks/DuplicateHouseholdMemberDialog";
import {
  HouseholdMemberAndRelationForm,
  HouseholdMemberAndRelationFormParamsType,
} from "@/blocks/HouseholdMemberAndRelationForm";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnForm } from "@/components/VnForm";
import { useAddHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import {
  useCurrentHouseholdMemberQuery,
  useListHouseholdMembersQuery,
} from "@/data/queries/householdMembersQueries";
import {
  HouseholdMemberAndRelationshipFormType,
  HouseholdMemberType,
  HouseholdMemberWithRelationshipType,
} from "@/domain/householdMember";
import { useCesSurvey } from "@/hooks/useCesSurvey";
import { useHouseholdMemberAndRelationForm } from "@/hooks/useHouseholdMemberAndRelationForm";
import { useTranslator } from "@/hooks/useTranslate";
import { I18nKeyType } from "@/i18n";
import { CesSurveyPrompt } from "@/interactors/CesSurveyPrompt";
import { hasMatchingHouseholdMemberName } from "@/utils/householdMemberName";

// import { HouseholdMemberCard } from "@/interactors/HouseholdMemberCard";

type HouseholdMemberAndRelationshipValueType =
  HouseholdMemberAndRelationshipFormType[keyof HouseholdMemberAndRelationshipFormType];

type PendingHouseholdMemberSubmissionType = {
  data: HouseholdMemberAndRelationshipFormType;
  shouldNavigateNextStep: boolean;
};

export type AddHouseholdMemberScreenPropsType =
  HouseholdMemberAndRelationFormParamsType & {
    onSuccessOrContinue: (
      addedHouseholdMember: HouseholdMemberType | null,
    ) => void;
    shouldShowAddAnotherButton?: boolean;
    continueSaveButtonI18nKey?: I18nKeyType;
    onAddAnother?: () => void;
  };

export const AddHouseholdMemberScreen: FC<
  AddHouseholdMemberScreenPropsType
> = ({
  onSuccessOrContinue,
  ageRangeOptions,
  assumptions,
  relationshipOptions,
  shouldShowAddAnotherButton = true,
  continueSaveButtonI18nKey = "common:continue",
  onAddAnother,
}) => {
  const { t } = useTranslator();

  const [isAddAnotherLoading, setIsAddAnotherLoading] = useState(false);
  const [isContinueLoading, setIsContinueLoading] = useState(false);
  const [duplicateMember, setDuplicateMember] =
    useState<HouseholdMemberWithRelationshipType | null>(null);
  const pendingSubmissionRef =
    useRef<PendingHouseholdMemberSubmissionType | null>(null);
  const isSubmissionLockedRef = useRef(false);

  const { data: currentHouseholdMember } = useCurrentHouseholdMemberQuery();
  const { refetch: refetchHouseholdMembers } = useListHouseholdMembersQuery();

  const { mutate: addHouseholdMember, isPending: isHouseholdMemberPending } =
    useAddHouseholdMemberMutation();

  const { showCesSurveyThenContinue, cesSurveyProps } = useCesSurvey({
    triggerPoint: "HOUSEHOLD_MEMBER_ADDED",
  });
  const hasAddedMemberRef = useRef(false);
  const { form, setShouldShowDateOfBirthInput } =
    useHouseholdMemberAndRelationForm();

  const { setValue, watch } = form;
  const isSameMailingAddressChecked = watch("sameMailingAddress");
  const isLivingWithStatusChecked = watch(
    "householdMemberRelationship.livingWithStatus",
  );

  const preLoadAssumptions = useCallback(() => {
    if (assumptions) {
      Object.entries(assumptions).forEach(([key, value]) => {
        if (value !== undefined) {
          setValue(
            key as keyof HouseholdMemberAndRelationshipFormType,
            value as HouseholdMemberAndRelationshipValueType,
          );
        }
      });
    }
  }, [assumptions, setValue]);

  const preLoadMailingAddress = useCallback(() => {
    // If the same mailing address is checked, populate the mailing address fields based on the current household member's address
    if (isSameMailingAddressChecked && currentHouseholdMember) {
      setValue(
        "mailingAddressCity",
        currentHouseholdMember.mailingAddressCity ?? null,
      );
      setValue(
        "mailingAddressState",
        currentHouseholdMember.mailingAddressState ?? null,
      );
      setValue(
        "mailingAddressStreetOne",
        currentHouseholdMember.mailingAddressStreetOne ?? null,
      );
      setValue(
        "mailingAddressStreetTwo",
        currentHouseholdMember.mailingAddressStreetTwo ?? null,
      );
      setValue(
        "mailingAddressComplement",
        currentHouseholdMember.mailingAddressComplement ?? null,
      );
      setValue(
        "mailingAddressZip",
        currentHouseholdMember.mailingAddressZip ?? null,
      );
      setValue("mailingCounty", currentHouseholdMember.mailingCounty ?? null);
    } else {
      setValue("mailingAddressCity", null);
      setValue("mailingAddressState", null);
      setValue("mailingAddressStreetOne", null);
      setValue("mailingAddressStreetTwo", null);
      setValue("mailingAddressComplement", null);
      setValue("mailingAddressZip", null);
      setValue("mailingCounty", null);
    }
  }, [currentHouseholdMember, isSameMailingAddressChecked, setValue]);

  const preLoadMainAddress = useCallback(() => {
    // If is living with me is checked, populate the address fields based on the current household member's address
    if (isLivingWithStatusChecked && currentHouseholdMember) {
      setValue(
        "mainAddressCity",
        currentHouseholdMember.mainAddressCity ?? null,
      );
      setValue(
        "mainAddressState",
        currentHouseholdMember.mainAddressState ?? null,
      );
      setValue(
        "mainAddressStreetOne",
        currentHouseholdMember.mainAddressStreetOne ?? null,
      );
      setValue(
        "mainAddressStreetTwo",
        currentHouseholdMember.mainAddressStreetTwo ?? null,
      );
      setValue(
        "mainAddressComplement",
        currentHouseholdMember.mainAddressComplement ?? null,
      );
      setValue("mainAddressZip", currentHouseholdMember.mainAddressZip ?? null);
      setValue("mainCounty", currentHouseholdMember.mainCounty ?? null);
    } else {
      setValue("mainAddressCity", null);
      setValue("mainAddressState", null);
      setValue("mainAddressStreetOne", null);
      setValue("mainAddressStreetTwo", null);
      setValue("mainAddressComplement", null);
      setValue("mainAddressZip", null);
      setValue("mainCounty", null);
    }
  }, [currentHouseholdMember, isLivingWithStatusChecked, setValue]);

  useEffect(() => {
    preLoadAssumptions();
  }, [preLoadAssumptions]);

  useEffect(() => {
    preLoadMailingAddress();
  }, [preLoadMailingAddress]);

  useEffect(() => {
    preLoadMainAddress();
  }, [preLoadMainAddress]);

  useEffect(() => {
    return () => {
      isSubmissionLockedRef.current = false;
      pendingSubmissionRef.current = null;
    };
  }, []);

  const firstNameValue = form.watch("firstName");
  const lastNameValue = form.watch("lastName");

  const resetSubmissionState = () => {
    isSubmissionLockedRef.current = false;
    setIsContinueLoading(false);
    setIsAddAnotherLoading(false);
  };

  const saveHouseholdMember = (
    data: HouseholdMemberAndRelationshipFormType,
    shouldNavigateNextStep: boolean,
  ) => {
    if (shouldNavigateNextStep) {
      setIsContinueLoading(true);
    } else {
      setIsAddAnotherLoading(true);
    }

    addHouseholdMember(
      {
        ...data,
        householdMemberRelationship: {
          relationship: data.householdMemberRelationship.relationship,
          otherRelationship: data.householdMemberRelationship.otherRelationship,
          taxRelationship: data.householdMemberRelationship.taxRelationship,
          livingWithStatus: data.householdMemberRelationship.livingWithStatus
            ? "LIVING_WITH"
            : "NOT_LIVING_WITH",
        },
      },
      {
        onSuccess: addedHouseholdMember => {
          toast.success(
            t("native:addHouseholdMemberScreen.formSubmission.success", {
              firstName: data.firstName,
            }),
          );

          setShouldShowDateOfBirthInput(false);

          form.reset();
          hasAddedMemberRef.current = true;

          if (shouldNavigateNextStep) {
            showCesSurveyThenContinue(() =>
              onSuccessOrContinue?.(addedHouseholdMember ?? null),
            );
          } else {
            preLoadAssumptions();
            preLoadMailingAddress();
            preLoadMainAddress();
            onAddAnother?.();
          }
        },
        onSettled: resetSubmissionState,
      },
    );
  };

  const handleFormSubmit = async (shouldNavigateNextStep: boolean) => {
    if (isSubmissionLockedRef.current) return;

    isSubmissionLockedRef.current = true;
    setIsContinueLoading(shouldNavigateNextStep);
    setIsAddAnotherLoading(!shouldNavigateNextStep);

    const { data: members, isError } = await refetchHouseholdMembers({
      cancelRefetch: false,
    });

    if (!isSubmissionLockedRef.current) return;

    if (isError || !members) {
      resetSubmissionState();
      toast.error(
        t("native:addHouseholdMemberScreen.duplicateDialog.checkError"),
      );
      return;
    }

    // The patient can edit the form while the member list is loading.
    await form.handleSubmit(data => {
      if (!isSubmissionLockedRef.current) return;

      const matchingMember = members.find(member =>
        hasMatchingHouseholdMemberName({ member, candidate: data }),
      );

      if (matchingMember) {
        pendingSubmissionRef.current = { data, shouldNavigateNextStep };
        setDuplicateMember(matchingMember);
        setIsContinueLoading(false);
        setIsAddAnotherLoading(false);
        return;
      }

      saveHouseholdMember(data, shouldNavigateNextStep);
    }, resetSubmissionState)();
  };

  const handleDuplicateDialogConfirm = () => {
    const pendingSubmission = pendingSubmissionRef.current;
    if (!pendingSubmission) return;

    pendingSubmissionRef.current = null;
    setDuplicateMember(null);
    saveHouseholdMember(
      pendingSubmission.data,
      pendingSubmission.shouldNavigateNextStep,
    );
  };

  const handleDuplicateDialogClose = () => {
    if (!pendingSubmissionRef.current) return;

    pendingSubmissionRef.current = null;
    isSubmissionLockedRef.current = false;
    setDuplicateMember(null);
  };

  const isSubmissionPending =
    isHouseholdMemberPending ||
    isAddAnotherLoading ||
    isContinueLoading ||
    Boolean(duplicateMember);

  // Let's only validate the form if at least one of the name fields is filled
  const shouldValidateForm = firstNameValue !== "" || lastNameValue !== "";

  return (
    <>
      <VnCard className="rounded-md">
        <VnForm form={form} isInputOnlyForm>
          <HouseholdMemberAndRelationForm
            isLoading={false}
            onChangeDateOfBirthState={setShouldShowDateOfBirthInput}
            params={{
              ageRangeOptions,
              assumptions,
              relationshipOptions,
            }}
          />
        </VnForm>
      </VnCard>

      <VnBox className="gap-2">
        {shouldShowAddAnotherButton && (
          <VnButton
            i18nKey="native:addHouseholdMemberScreen.addAnother"
            isDisabled={isSubmissionPending}
            isLoading={isAddAnotherLoading}
            onPress={() => {
              void form.handleSubmit(() => handleFormSubmit(false))();
            }}
          />
        )}
        <VnButton
          i18nKey={continueSaveButtonI18nKey}
          variant={!shouldShowAddAnotherButton ? "default" : "outline"}
          isDisabled={isSubmissionPending}
          isLoading={isContinueLoading}
          onPress={() => {
            if (isSubmissionLockedRef.current) return;

            if (shouldValidateForm) {
              void form.handleSubmit(() => handleFormSubmit(true))();
            } else if (hasAddedMemberRef.current) {
              showCesSurveyThenContinue(() => onSuccessOrContinue(null));
            } else {
              onSuccessOrContinue(null);
            }
          }}
        />
      </VnBox>

      <DuplicateHouseholdMemberDialog
        member={duplicateMember}
        currentHouseholdMemberId={currentHouseholdMember?.id}
        onClose={handleDuplicateDialogClose}
        onConfirm={handleDuplicateDialogConfirm}
      />
      <CesSurveyPrompt {...cesSurveyProps} />
    </>
  );
};
